import React, { useEffect } from "react";
import { useSnackbar } from '../contexts/SnackbarProvider';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, InputAdornment,
    Button, TextField, FormControlLabel, Checkbox, Stack, Switch
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import axiosInstance from "../utils/axiosInstance";
import AutocompleteInput from "../components/AutocompleteInput";

const SupplierProductModal = ({ open, onClose, product, supplierId, onSaved, onDeleted, hasLimit }) => {
    const { showSnackbar } = useSnackbar();
    const { control, setValue, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm({
        defaultValues: {
            variant_id: "",
            supplier_product_code: "",
            supplier_product_name: "",
            purchase_price: "",
            is_primary: false,
            status: "active",
        },
    });

    // 編集モードなら初期値セット
    useEffect(() => {
        reset({
            variant_id: product?.variant_id || "",
            sku: product?.sku || "",
            supplier_product_code: product?.supplier_product_code || "",
            supplier_product_name: product?.supplier_product_name || "",
            purchase_price: product?.purchase_price != null
                ? Number(product.purchase_price)
                : "",
            is_primary: product?.is_primary || false,
            status: product?.status || "active",
        });
    }, [product, reset]);

    const onSubmit = async (data) => {
        try {
            const payload = { ...data, supplier_id: supplierId };
            const res = product
                ? await axiosInstance.put(`/supplier/product/${product.variant_id}`, payload)
                : await axiosInstance.post(`/supplier/product`, payload);
            onSaved?.(res.data);
            onClose();
        } catch (err) {
            if (process.env.NODE_ENV === 'development') console.error(err);
            showSnackbar(err?.response?.data?.message || '保存に失敗しました', 'error');
        }
    };

    const handleDelete = async () => {
        if (!product) return;
        if (!window.confirm("この取扱商品を削除しますか？")) return;
        try {
            const payload = {
                ...product,
                supplier_id: supplierId,
                status: "invalid"
            };

            const res = await axiosInstance.put(
                `/supplier/product/${product.variant_id}`,
                payload
            );
            onDeleted?.(product.variant_id);
            showSnackbar(res.data?.message || '削除しました', 'success');
            onClose();
        } catch (err) {
            if (process.env.NODE_ENV === 'development') console.error(err);
            showSnackbar(err?.response?.data?.message || '削除に失敗しました', 'error');
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{product ? "商品編集" : "商品追加"}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} mt={1}>
                    {/* 商品選択 */}
                    <Controller
                        name="sku"
                        control={control}
                        rules={{
                            required: '品番は必須です',
                            maxLength: {
                                value: 100,
                                message: '100文字以内で入力してください',
                            },
                        }}
                        render={({ field }) => (
                            <AutocompleteInput
                                hasLimit={hasLimit}
                                label='品番'
                                value={field.value || ''}
                                isEditMode={true}
                                fetchSuggestions={async (keyword) => {
                                    try {
                                        const res = await axiosInstance.post('/product/sku_suggest', { keyword });
                                        return res.data;
                                    } catch (err) {
                                        console.error(err);
                                        return [];
                                    }
                                }}
                                onChange={(e, idx, selectedProduct) => {
                                    field.onChange(selectedProduct?.sku || e?.target?.value || "");
                                    setValue('variant_id', selectedProduct?.variant_id || '');
                                }}
                                error={!!errors.sku}
                                helperText={errors.sku?.message}
                            />
                        )}
                    />
                    {/* 仕入先コード */}
                    <Controller
                        name="supplier_product_code"
                        control={control}
                        rules={{ required: '仕入先商品コードは必須です', maxLength: { value: 50, message: "50文字以内で入力してください" } }}
                        render={({ field, fieldState }) => (
                            <TextField
                                label="仕入先商品コード"
                                {...field}
                                inputProps={{ maxLength: 50 }}
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                            />
                        )}
                    />

                    {/* 仕入先商品名 */}
                    <Controller
                        name="supplier_product_name"
                        control={control}
                        rules={{ required: '仕入先商品名は必須です', maxLength: { value: 255, message: "255文字以内で入力してください" } }}
                        render={({ field, fieldState }) => (
                            <TextField
                                label="仕入先商品名"
                                {...field}
                                inputProps={{ maxLength: 255 }}
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                                multiline minRows={1}
                            />
                        )}
                    />
                    {/* 仕入価格 */}
                    <Controller
                        name="purchase_price"
                        control={control}
                        rules={{
                            required: "仕入価格は必須です",
                            min: { value: 0, message: "0以上で入力してください" },
                            max: { value: 99999999, message: "8桁以内で入力してください" }
                        }}
                        render={({ field, fieldState }) => (
                            <TextField
                                label="仕入価格（税抜）"
                                fullWidth
                                type="number"
                                {...field}
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">円</InputAdornment>
                                }}
                            />
                        )}
                    />
                    {/* メイン商品 
                    <Controller
                        name="is_primary"
                        control={control}
                        render={({ field }) => (
                            <FormControlLabel
                                control={<Checkbox checked={field.value} {...field} />}
                                label="メイン商品"
                            />
                        )}
                    />
*/}
                    {/* 有効 / 無効 */}
                    {product && (
                        <Controller
                            name="status"
                            control={control}
                            render={({ field }) => (
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={field.value === "active"}
                                            onChange={(e) => field.onChange(e.target.checked ? "active" : "pending")}
                                        />
                                    }
                                    label="有効"
                                />
                            )}
                        />
                    )}
                </Stack>
            </DialogContent>

            <DialogActions>
                {product && (
                    <Button color="error" onClick={handleDelete} disabled={isSubmitting} variant="outlined" sx={{ mr: 'auto' }}>
                        削除する
                    </Button>
                )}
                <Button onClick={onClose} disabled={isSubmitting} variant="outlined">キャンセル</Button>
                <Button variant="contained" onClick={handleSubmit(onSubmit)} disabled={isSubmitting}>
                    保存
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SupplierProductModal;